import { normalizeHex, parseHexRgb, rgbToHex } from '../utils/color'

/* ------------------------------------------------------------------ */
/*  Non-component helpers for the shared drawer controls                */
/*                                                                      */
/*  Split out of drawerControls.tsx: react-refresh requires .tsx files  */
/*  to export only components for HMR to stay reliable, and these are   */
/*  pure class-string builders + color/opacity codecs shared by the     */
/*  settings modal and both settings drawers.                           */
/* ------------------------------------------------------------------ */

/**
 * Primary action button.
 *
 * The disabled state used to be `bg-border` + `text-primary-foreground` at 60%
 * opacity. On a raised panel that is a 1.2:1 contrast ratio — the button was
 * effectively invisible. `--primary-foreground` is DARK in dark mode by design
 * (it sits on the light `--primary` fill), so reusing it over a dark disabled
 * fill put dark text on dark, then faded it further.
 *
 * Disabled now reads as recessed rather than absent: a muted fill with the
 * app's secondary-text token (~7:1, comfortably legible). The "you can't press
 * this" signal comes from losing the primary FILL and the cursor — not from
 * being unreadable. A control the user cannot see is a bug; one they can read
 * but not press is just disabled.
 */
export function primaryButtonCls(disabled?: boolean): string {
  return (
    'h-7 rounded-sm px-3.5 text-xs font-semibold transition-colors ' +
    (disabled
      ? 'cursor-default bg-muted text-[hsl(var(--muted-foreground))]'
      : 'bg-primary text-primary-foreground hover:bg-primary-hover')
  )
}

/** Underlined segmented tab-bar button classes, keyed on the active state.
 *  Shared by the settings modal + indicator/overlay drawers. */
export function drawerTabCls(active: boolean): string {
  return (
    'border-b-2 px-3 py-2 text-[11px] font-medium transition-colors ' +
    (active
      ? 'border-primary text-[hsl(var(--chart-text))]'
      : 'border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--chart-text))]')
  )
}

/** Hex + 0–1 opacity → the single color string a picker edits: plain hex at
 *  full opacity, `rgba(...)` otherwise. Inverse of `decomposeColorOpacity`. */
export function composeColorOpacity(hex: string, opacity: number): string {
  if (opacity >= 1) return hex
  if (opacity <= 0) return 'transparent'
  const { r, g, b } = parseHexRgb(hex)
  return `rgba(${r},${g},${b},${opacity.toFixed(2)})`
}

export function decomposeColorOpacity(value: string): { hex: string; alpha: number } {
  if (value === 'transparent') return { hex: '#000000', alpha: 0 }
  const rgbaMatch = value.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)/,
  )
  if (rgbaMatch) {
    const a = rgbaMatch[4] !== undefined ? Number(rgbaMatch[4]) : 1
    return {
      hex: rgbToHex(Number(rgbaMatch[1]), Number(rgbaMatch[2]), Number(rgbaMatch[3])),
      alpha: a,
    }
  }
  return { hex: normalizeHex(value), alpha: 1 }
}

/** Native <input type="color"> only accepts hex. Strip alpha for the fallback. */
export function extractHex(value: string): string {
  const rgbaMatch = value.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/)
  if (rgbaMatch) {
    return rgbToHex(Number(rgbaMatch[1]), Number(rgbaMatch[2]), Number(rgbaMatch[3]))
  }
  return value.startsWith('#') ? value : '#000000'
}
